import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import { LogoLockup } from "@/components/BrandLogos";

const links = [
  { label: "ComplianceMinder", to: "/complianceminder" },
  { label: "CaseMinder", to: "/caseminder" },
  { label: "Training", to: "/training" },
  { label: "How it works", to: "/#how-it-works" },
  { label: "For IT teams", to: "/playbook" },
  { label: "Resources", to: "/resources" },
  { label: "Insights", to: "/insights" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-sm">
      <div className="mx-auto flex w-full max-w-[1360px] items-center justify-between px-6 py-4 md:px-10 lg:px-16">
        {/* Brand */}
        <Link to="/" data-testid="nav-brand" className="group">
          <LogoLockup iconClassName="h-9 w-9" />
        </Link>

        {/* Desktop links */}
        <nav className="hidden items-center gap-6 xl:flex 2xl:gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap text-sm font-medium tracking-wide transition-colors hover:text-accent ${
                  isActive ? "text-accent" : "text-foreground/80"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link
            to="/book-a-walkthrough"
            data-testid="nav-cta-walkthrough"
            className="hidden bg-primary px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-primary-foreground transition-colors hover:bg-accent hover:text-accent-foreground md:inline-block"
          >
            Book a walkthrough
          </Link>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center border border-border text-foreground xl:hidden"
          >
            {open ? <X strokeWidth={1.6} className="h-5 w-5" /> : <Menu strokeWidth={1.6} className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-border bg-background xl:hidden">
          <nav className="mx-auto flex w-full max-w-[1360px] flex-col px-6 py-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `border-b border-border py-4 text-sm font-medium tracking-wide transition-colors hover:text-accent ${
                    isActive ? "text-accent" : "text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/book-a-walkthrough"
              data-testid="nav-mobile-cta"
              className="mt-4 mb-3 bg-primary px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.12em] text-primary-foreground"
            >
              Book a walkthrough
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
